// The one prompt every door in shows when a chosen file already exists where it
// is going: its name is a raw file there, or is waiting in that table's
// unallocated queue. The user decides — upload anyway, skip the ones that exist,
// or cancel (H28). `conflicts` are { file, where? }: `where` names the table(s)
// holding the file when the door could not know its destination.
import React, { useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

function ExistingFilesPrompt({ open, conflicts, total, scope, onCancel, onSkip, onUploadAnyway }) {
  // The dialog stays mounted through its closing fade after the parent clears
  // its state, so the text keeps naming the last files rather than an empty list.
  const shownRef = useRef({ conflicts: [], total: 0, scope: '' });
  if (open) shownRef.current = { conflicts: conflicts || [], total, scope };
  const shown = shownRef.current;
  const single = shown.conflicts.length === 1;
  const nothingNew = shown.conflicts.length >= shown.total;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{single ? 'File Already Exists' : 'Files Already Exist'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {single
            ? `"${shown.conflicts[0].file.name}" already exists ${shown.scope}. Uploading it again will create a duplicate.`
            : `${shown.conflicts.length} of the ${shown.total} selected files already exist ${shown.scope}. Uploading them again will create duplicates.`}
        </DialogContentText>
        <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 3 }}>
          {shown.conflicts.map(({ file, where }, index) => (
            <Typography key={`${file.name}-${index}`} component="li" variant="body2">
              {file.name}{where ? ` — in ${where}` : ''}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        {/* Nothing new left to upload once every chosen file exists. */}
        <Button onClick={onSkip} disabled={nothingNew}>Skip Duplicates</Button>
        <Button onClick={onUploadAnyway} color="primary">Upload Anyway</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExistingFilesPrompt;
